import { useContext, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  BoxFull,
  Button,
  Dropdown,
  TransactionalSubheader,
} from "iuvity-ui-components";
import { CustomInput, Header } from "../../components";
import {
  NewCustomDialog,
  NewDialogProps,
} from "../../components/dialog/NewCustomDialog";
import { useMappedFields } from "../../hooks/useMappedFields";
import {
  MP_DASHBOARD_OPTIONS,
  MP_DATATYPES_OPTIONS,
  MP_SERVICEAPI_OPTIONS,
} from "../../constants";
import { MappedFieldSchema } from "../../validations/schemas";
import { AppContext } from "../../context/index";
import { ErrorMessage } from "@hookform/error-message";
import { useVersions } from "../../hooks/useVersions";
import { useTranslations } from "../../hooks/useTranslations";
import { Version } from "../../interfaces/BDModels";
import { FieldsLinkedTocreateLabelsButton, REVALIDATE_FORM_PROPS } from "../../constants";
import { createLabelButtonMaker } from "../../helpers/createLabelButtonMaker";

interface NewCMappedFieldProps {
  update?: boolean;
}

const formBlankValues = {
  name: "",
  description: "",
  labelkey: "",
  descriptionlabelkey: "",
  jsonpath: "",
  extrajsonpath: "",
  serviceapi: "",
  dashboardoption: "",
  datatype: "",
  releaseversion: "",
};

const formKeysOptions = {
  serviceapi: MP_SERVICEAPI_OPTIONS,
  dashboardoption: MP_DASHBOARD_OPTIONS,
  datatype: MP_DATATYPES_OPTIONS,
  labelkey: (t:Function) => [],
};

export const NewMappedFieldPage: React.FC<NewCMappedFieldProps> = ({
  update = false,
}) => {
  const navigate = useNavigate();
  const {
    t,
    appState: { user, selectedMappedField },
    setAppState,
  } = useContext(AppContext);

  const {
    control,
    getValues,
    setValue,
    formState: { errors, isDirty, isValid },
  } = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: update
      ? {
          id: selectedMappedField?.id,
          name: selectedMappedField?.name,
          description: selectedMappedField?.description,
          labelkey: selectedMappedField?.labelkey,
          descriptionlabelkey: selectedMappedField?.descriptionlabelkey,
          jsonpath: selectedMappedField?.jsonpath,
          extrajsonpath: selectedMappedField?.extrajsonpath,
          serviceapi: selectedMappedField?.serviceapi,
          dashboardoption: selectedMappedField?.dashboardoption,
          datatype: selectedMappedField?.datatype,
          releaseversion: selectedMappedField?.releaseversion,
          updatedby: user?.name,
        }
      : {
          ...formBlankValues,
          createdby: user?.name,
        },
  });

  const { mappedFieldAction } = useMappedFields("ACTIONS");
  const { data: versionsData } = useVersions("DATA");
  const { data: labelsData, reloadFn } = useTranslations("DATA");

  const [modalProps, setModalProps] = useState<NewDialogProps>({
    visible: false,
  } as NewDialogProps);

  const onReturn = () => {
    setAppState((prev) => ({
      ...prev,
      selectedConfig: null,
    }));
    navigate(-1);
    window.scrollTo(0, 0);
  };

  const onAction = async () => {
    const formValues = getValues();
    const actionResult = await mappedFieldAction(update ? "UPDATE" : "CREATE", [
      formValues,
    ]);
    if (actionResult === "SUCCESS") {
      navigate("/mappedFields");
      window.scrollTo(0, 0);
    }
  };

  const handleModalProps = (actionType: "action" | "cancel") => {
    switch (actionType) {
      case "action":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t(`MappedFields.${update ? "save" : "create"}ModalTitle`),
          content: t(`MappedFields.${update ? "save" : "create"}ModalContent`),
          onConfirm: onAction,
        }));
      case "cancel":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("MappedFields.cancelModalTitle"),
          content: t("MappedFields.cancelModalContent"),
          onConfirm: onReturn,
        }));
      default:
        return;
    }
  };

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}

      <TransactionalSubheader
        subheaderTitle={
          <div style={{ position: "relative", left: "-40px" }}>
            <i
              className="iu iu-icon-left pr-3"
              onClick={onReturn}
              aria-hidden="true"
              style={{
                fontSize: "var(--font-size-tablet-title)",
                cursor: "pointer",
              }}
            />
            {update ? t("MappedFields.editTitle") : t("MappedFields.newTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={t("MappedFields.mappedFieldsTitle")}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("MappedFields.mappedFieldsTitle"),
              command: () => onReturn(),
              style: { fontFamily: "var(--fontBarlowSemiBold)" },
            },
            {
              label: update
                ? t("MappedFields.editTitle")
                : t("MappedFields.newTitle"),
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
      </TransactionalSubheader>

      <BoxFull
        className="flex align-items-center justify-content-center my-4"
        variant="tertiary"
      >
        <div className="form-container md py-5">
          <div className="form md">
            {Object.keys(formBlankValues)
              .filter((key) => key !== "releaseversion")
              .map((key, idx, arr) => (
                <>
                  <Controller
                    name={key as any}
                    control={control}
                    rules={
                      update &&
                      key === "description" &&
                      !selectedMappedField?.createdby
                        ? (MappedFieldSchema(t) as any)["descriptionvs2"]
                        : (MappedFieldSchema(t) as any)[key]
                    }
                    render={({ field }) => {
                      const extraProps = {
                        keyfilter: (MappedFieldSchema(t) as any)[key]
                          ?.keyfilter,
                        maxLength: (MappedFieldSchema(t) as any)[key]?.maxLength
                          ?.value,
                      };
                      return Object.keys(formKeysOptions).includes(key) ? (
                        // @ts-ignore
                        <div className={FieldsLinkedTocreateLabelsButton.includes(key) ? 'mb-3' : ''}>
                          <Dropdown
                            className={`dropdown mb-3`}
                            label={t(`MappedFields.${key}Label`)}
                            placeholder={t(`MappedFields.${key}Placeholder`)}
                            options={
                              (labelsData && key === 'labelkey')
                                ? (()=>{
                                    let labelsOptions = labelsData?.filter((item: any) => item?.widgets)
                                      ?.map((item: any)=> ({ label: item?.key, value: item?.key}));
                                    return update ? [ ...labelsOptions, {label: selectedMappedField?.labelkey, value: selectedMappedField?.labelkey}] : labelsOptions;
                                  })()
                                  ?.sort((a: any, b: any) => a.label.localeCompare(b.label))
                                : (formKeysOptions as any)[key](t)
                            }
                            filter={key === 'labelkey'}
                            showClear={false}
                            width="20rem"
                            {...field}
                          />
                          {
                            FieldsLinkedTocreateLabelsButton.includes(key) &&
                              createLabelButtonMaker({
                                setModalProps,
                                dialogTitle: t('ConsoleTranslations.createLabelTitle'),
                                dialogDetail: t('ConsoleTranslations.createLabelDescription'),
                                buttonLabel: t('ConsoleTranslations.createLabelTitle'),
                                functionality:'MappedFields',
                                actionsToExecute:(key: string)=>{
                                  reloadFn();
                                  setValue("labelkey", key, REVALIDATE_FORM_PROPS)
                                },
                              })
                          }
                        </div>
                      ) : (
                        <CustomInput
                          id={`input-label`}
                          label={t(`MappedFields.${key}Label`)}
                          placeholder={t(`MappedFields.${key}PlaceholderLabel`)}
                          type="text"
                          autoComplete="off"
                          className={
                            // @ts-ignore
                            errors[key]?.message ? "p-invalid" : "mb-4"
                          }
                          style={{ width: "20rem" }}
                          {...field}
                          {...extraProps}
                        />
                      );
                    }}
                  />
                  {!Object.keys(formKeysOptions).includes(key) && (
                    <ErrorMessage
                      errors={errors}
                      name={key as any}
                      //@ts-ignore
                      render={({ message }) => (
                        <small
                          className="p-error block"
                          style={{
                            marginTop: "0.5rem",
                            marginBottom: "1.5rem",
                          }}
                        >
                          {message}
                        </small>
                      )}
                    />
                  )}
                </>
              ))}

            {versionsData && (
              <div key="releaseversion">
                <Controller
                  key="releaseversion"
                  name="releaseversion"
                  control={control}
                  rules={(MappedFieldSchema(t) as any).releaseversion}
                  render={({ field }) => {
                    return (
                      // @ts-ignore
                      <Dropdown
                        id="im-type-dropdown"
                        className="dropdown mb-3"
                        label={t("MappedFields.releaseversionLabel")}
                        placeholder={t(
                          "MappedFields.releaseversionPlaceholder"
                        )}
                        options={versionsData
                          .map((ver: Version) => ({
                            label: ver.name,
                            value: ver.name,
                          }))
                          .sort((a: any, b: any) =>
                            b.label.localeCompare(a.label)
                          )}
                        filter={false}
                        showClear={false}
                        style={{
                          zIndex: "2300 !important",
                        }}
                        {...field}
                      />
                    );
                  }}
                />
                <ErrorMessage
                  errors={errors}
                  name="releaseversion"
                  //@ts-ignore
                  render={({ message }) => (
                    <small
                      className="p-error block"
                      style={{ marginTop: "0.5rem", marginBottom: "1.5rem" }}
                    >
                      {message}
                    </small>
                  )}
                />
              </div>
            )}
          </div>
        </div>
      </BoxFull>

      <BoxFull className="my-5 p-0" style={{ backgroundColor: "transparent" }}>
        <div className="flex align-items-center justify-content-end gap-5">
          <Button
            variant="p-button-link"
            label={t("MappedFields.cancelBtnLabel")}
            onClick={isDirty ? () => handleModalProps("cancel") : onReturn}
          />
          <Button
            variant="p-button"
            label={
              update
                ? t("TableActions.editLabel")
                : t("MappedFields.createBtnLabel")
            }
            disabled={update ? !(isValid && isDirty) : !isValid}
            onClick={() => handleModalProps("action")}
          />
        </div>
      </BoxFull>

      <NewCustomDialog
        title={modalProps.title}
        visible={modalProps.visible}
        content={modalProps.content}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
        aditionalClass="new-mappedfield-page-dialog overlay-createlabelsbutton"
        acceptLabel={modalProps.acceptLabel}
        rejectLabel={modalProps.rejectLabel}
        onReject={modalProps.onReject}
      />
    </>
  );
};
